import React, { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { ProfileProvider } from "../context/ProfileContext";
import {
  getApprovalStatus,
  getRole,
  getStageNumber,
  getToken,
  storeApprovalStatus,
  storeStageNumber,
} from "../utils/auth-token-utils/cookie";
import { BabysitterProvider } from "../context/BabysitterContext";
import { WebSocketProvider } from "../context/WebSocketContext";
import { getStage } from "../services/NetworkServices";
import { useSupervisorAutoLogout } from "../hooks/useSupervisorAutoLogout";

const WAITING_PATH = "/babysitter/waitingForApproval";
const CREATE_PROFILE_PATH = "/babysitter/createProfile";

const BabysitterProtectedRoute: React.FC = () => {
  const token = getToken();
  const role = getRole();
  const { pathname } = useLocation();

  useSupervisorAutoLogout();

  const [approvalStatus, setApprovalStatus] = useState<string | null>(
    getApprovalStatus(),
  );
  const [stage, setStage] = useState<string | null>(getStageNumber());
  const [isLoading, setIsLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    const syncApprovalStatus = async (): Promise<void> => {
      setIsLoading(true);
      try {
        const response = await getStage();
        if (cancelled) return;
        const data = response.data;
        const status: string | null = data?.data?.approvalStatus ?? null;
        const stageValue: string | null = data?.data?.stage ?? null;
        if (status !== null) {
          storeApprovalStatus(status);
          setApprovalStatus(status);
        }
        if (stageValue !== null) {
          storeStageNumber(stageValue);
          setStage(stageValue);
        }
      } catch {
        if (cancelled) return;
        setApprovalStatus(getApprovalStatus());
        setStage(getStageNumber());
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    syncApprovalStatus();

    return () => {
      cancelled = true;
    };
  }, [pathname]);

  if (!token || role !== "Supervisor") {
    return <Navigate to="/" replace />;
  }

  const isPending = approvalStatus !== null && approvalStatus !== "approved";
  const profileIncomplete = stage === "1";

  if (!isLoading) {
    if (profileIncomplete && pathname !== CREATE_PROFILE_PATH) {
      return <Navigate to={CREATE_PROFILE_PATH} replace />;
    }

    if (!profileIncomplete && isPending && pathname !== WAITING_PATH) {
      return <Navigate to={WAITING_PATH} replace />;
    }

    if (!profileIncomplete && !isPending && pathname === WAITING_PATH) {
      return <Navigate to="/supervisor/dashboard" replace />;
    }
  }

  return (
    <ProfileProvider>
      <BabysitterProvider>
        <WebSocketProvider>
          {isLoading ? null : <Outlet />}
        </WebSocketProvider>
      </BabysitterProvider>
    </ProfileProvider>
  );
};

export { BabysitterProtectedRoute };
